/**
 * Typing Detector Hook
 * 
 * Smart key filtering and typing state management for typing indicators.
 * Implements the lessons learned from the failed backend attempt - this is
 * completely separate from chat message pipeline.
 * 
 * Features:
 * - Smart key filtering (only content keys trigger typing)
 * - 5-second idle timeout with automatic stop
 * - Always sends typing_stop on message send (idempotent)
 * - WebSocket-only communication (like ping/pong)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { debugWS } from '../utils/debugWS';

// Configuration
const TYPING_TIMEOUT = 5000; // 5 seconds idle timeout
const MIN_INTERVAL_BETWEEN_STARTS = 800; // ensure quick subsequent starts during continuous typing pauses
const TYPING_START_DEBOUNCE = 100; // faster start to show indicators quickly

/**
 * Smart key filtering - only content keys should trigger typing indicators
 */
const isContentKey = (key) => {
  // Keys that should NOT trigger typing indicators
  const excludedKeys = [
    // Navigation keys
    'Enter', 'Return', 'Tab', 'Escape',
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
    'Home', 'End', 'PageUp', 'PageDown',
    
    // Modifier keys
    'Shift', 'Control', 'Alt', 'Meta', 'CapsLock',
    
    // Function keys
    'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
    
    // Other system keys
    'Insert', 'Delete', 'PrintScreen', 'ScrollLock', 'Pause'
  ];
  
  return !excludedKeys.includes(key);
};

/**
 * Send typing WebSocket message
 */
const sendTypingMessage = (socket, type, roomId) => {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    debugWS(`⌨️ TYPING DEBUG: ❌ Cannot send ${type} - WebSocket not connected (state: ${socket ? socket.readyState : 'null'})`);
    console.log(`⌨️ TYPING DEBUG: ❌ WebSocket not ready for ${type}`);
    return false;
  }
  
  try {
    const message = {
      type: type, // "typing_start" or "typing_stop"
      room_id: roomId,
      timestamp: Date.now()
    };
    
    socket.send(JSON.stringify(message));
    debugWS(`⌨️ TYPING DEBUG: ✅ Sent ${type} for room ${roomId}`);
    console.log(`⌨️ TYPING DEBUG: 📤 Sent WebSocket message: ${type} for room ${roomId}`);
    return true;
  } catch (error) {
    debugWS(`⌨️ TYPING DEBUG: ❌ Error sending ${type}:`, error);
    console.log(`⌨️ TYPING DEBUG: ❌ Error sending ${type}:`, error);
    return false;
  }
};

/**
 * Custom hook for typing detection and WebSocket communication
 */
export const useTypingDetector = (roomId, isConnected, getWebSocket) => {
  const [isTyping, setIsTyping] = useState(false);
  
  // Refs for managing timers and state
  const typingTimeoutRef = useRef(null);
  const startDebounceTimeoutRef = useRef(null);
  const lastKeyTimeRef = useRef(0);
  const isTypingRef = useRef(false); // Sync ref to avoid stale closures
  
  // Cleanup function to clear all timers
  const cleanup = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (startDebounceTimeoutRef.current) {
      clearTimeout(startDebounceTimeoutRef.current);
      startDebounceTimeoutRef.current = null;
    }
  }, []);
  
  // Send typing start (with debouncing)
  const sendTypingStart = useCallback(() => {
    // Prevent overly-frequent start events but allow quick start on fast typing
    const now = Date.now();
    if (now - lastKeyTimeRef.current < MIN_INTERVAL_BETWEEN_STARTS && isTypingRef.current) {
      // Already typing recently; skip redundant start
      return;
    }
    if (!roomId || !isConnected) {
      debugWS(`⌨️ TYPING DEBUG: Cannot send typing_start - roomId: ${roomId}, connected: ${isConnected}`);
      return;
    }
    
    // Clear any existing debounce
    if (startDebounceTimeoutRef.current) {
      clearTimeout(startDebounceTimeoutRef.current);
    }
    
    // Debounce typing start to avoid spam
    startDebounceTimeoutRef.current = setTimeout(() => {
      if (!isTypingRef.current) {
        const socket = getWebSocket();
        if (sendTypingMessage(socket, 'typing_start', roomId)) {
          setIsTyping(true);
          isTypingRef.current = true;
          debugWS(`⌨️ TYPING DEBUG: ✅ TYPING STARTED on this client for room ${roomId}`);
          console.log(`⌨️ TYPING STARTED on this client for room ${roomId}`);
        } else {
          debugWS(`⌨️ TYPING DEBUG: ❌ Failed to send typing_start`);
        }
      }
    }, TYPING_START_DEBOUNCE);
  }, [roomId, isConnected, getWebSocket]);
  
  // Send typing stop (immediate)
  const sendTypingStop = useCallback(() => {
    if (!roomId) {
      debugWS(`⌨️ TYPING DEBUG: Cannot send typing_stop - no roomId`);
      return;
    }
    
    // Clear all timers
    cleanup();
    
    // Only send if we were actually typing
    if (isTypingRef.current) {
      const socket = getWebSocket();
      if (socket) {
        sendTypingMessage(socket, 'typing_stop', roomId);
        debugWS(`⌨️ TYPING DEBUG: ✅ TYPING STOPPED on this client for room ${roomId}`);
        console.log(`⌨️ TYPING STOPPED on this client for room ${roomId}`);
      }
      
      setIsTyping(false);
      isTypingRef.current = false;
    } else {
      debugWS(`⌨️ TYPING DEBUG: typing_stop called but user wasn't typing`);
    }
  }, [roomId, getWebSocket, cleanup]);
  
  // Handle key down events
  const handleKeyDown = useCallback((event) => {
    const currentTime = Date.now();
    lastKeyTimeRef.current = currentTime;
    
    // Only process content keys - CHECK THIS FIRST
    if (!isContentKey(event.key)) {
      debugWS(`⌨️ TYPING DEBUG: Ignoring non-content key: ${event.key}`);
      return;
    }
    
    debugWS(`⌨️ TYPING DEBUG: Content key pressed: ${event.key} - triggering typing detection`);
    
    // Start typing if not already started
    if (!isTypingRef.current) {
      debugWS(`⌨️ TYPING DEBUG: User starting to type - will send typing_start`);
      sendTypingStart();
    } else {
      debugWS(`⌨️ TYPING DEBUG: User continuing to type - refreshing timeout`);
    }
    
    // Reset the idle timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set new idle timeout
    typingTimeoutRef.current = setTimeout(() => {
      // Double-check we haven't received newer key events
      if (Date.now() - lastKeyTimeRef.current >= TYPING_TIMEOUT - 100) {
        debugWS(`⌨️ TYPING DEBUG: Typing timeout reached (${TYPING_TIMEOUT}ms) - auto-stopping`);
        sendTypingStop();
      }
    }, TYPING_TIMEOUT);
  }, [sendTypingStart, sendTypingStop]);
  
  // Handle message send (always stop typing)
  const handleMessageSend = useCallback(() => {
    debugWS(`⌨️ TYPING DEBUG: Message sent - stopping typing indicator immediately`);
    console.log(`⌨️ TYPING DEBUG: Message sent - stopping typing indicator`);
    sendTypingStop();
  }, [sendTypingStop]);
  
  // Cleanup on unmount or room change
  useEffect(() => {
    return () => {
      cleanup();
      // Send final typing stop on unmount
      if (isTypingRef.current && roomId) {
        const socket = getWebSocket();
        if (socket) {
          sendTypingMessage(socket, 'typing_stop', roomId);
        }
      }
    };
  }, [roomId, cleanup, getWebSocket]);
  
  // Stop typing when disconnected
  useEffect(() => {
    if (!isConnected && isTypingRef.current) {
      debugWS(`⌨️ Disconnected - stopping typing indicator`);
      setIsTyping(false);
      isTypingRef.current = false;
      cleanup();
    }
  }, [isConnected, cleanup]);
  
  return {
    isTyping,
    handleKeyDown,
    handleMessageSend,
    sendTypingStop // For manual control
  };
};